import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import Icon from 'components/styleguide/icon/Icon';

const styles = require('./MonthYearPicker.module.scss');

const MONTHS_SHORT = moment.monthsShort();
const MONTHS_FULL = moment.months();

interface MonthYearPickerProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  value?: { month: number; year: number } | null;
  onChange: (value: { month: number; year: number } | null) => void;
  context?: 'talent' | 'company' | 'neutral';
  disabled?: boolean;
  minValue?: { month: number; year: number } | null;
  maxValue?: { month: number; year: number } | null;
}

export default function MonthYearPicker({
  label,
  placeholder = 'Select date',
  required = false,
  error,
  value,
  onChange,
  context = 'neutral',
  disabled = false,
  minValue,
  maxValue,
}: MonthYearPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'year' | 'month'>('year');
  const [tempYear, setTempYear] = useState<number | null>(value?.year ?? null);
  const [alignRight, setAlignRight] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectedYearRef = useRef<HTMLButtonElement>(null);

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const years = Array.from({ length: 80 }, (_, i) => currentYear - i);

  const displayValue = value ? `${MONTHS_FULL[value.month - 1]} ${value.year}` : '';

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && step === 'year' && selectedYearRef.current) {
      selectedYearRef.current.scrollIntoView({ block: 'center' });
    }
  }, [isOpen, step]);

  const handleOpen = () => {
    setTempYear(value?.year ?? null);
    setStep('year');
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const popoverWidth = 280;
      const margin = 16;
      setAlignRight(rect.left + popoverWidth > window.innerWidth - margin);
    }
    setIsOpen(true);
  };

  const handleYearClick = (year: number) => {
    setTempYear(year);
    setStep('month');
  };

  const handleMonthClick = (month: number) => {
    if (tempYear == null) return;

    onChange({ month, year: tempYear });
    setIsOpen(false);
  };

  const isMonthDisabled = (month: number) => {
    if (tempYear === currentYear && month > currentMonth) return true;
    if (minValue && tempYear === minValue.year && month < minValue.month) return true;
    if (maxValue && tempYear === maxValue.year && month > maxValue.month) return true;
    return false;
  };

  const isYearDisabled = (year: number) => {
    if (minValue != null && year < minValue.year) return true;
    if (maxValue != null && year > maxValue.year) return true;
    return false;
  };

  return (
    <div ref={wrapperRef} className={`${styles.container} ${styles[context]}`}>
      {label && (
        <div className={styles.labelContainer}>
          <label className={styles.label}>{label}</label>
          {required && <span className={styles.required}>*</span>}
        </div>
      )}
      <button
        type="button"
        className={error ? styles.inputError : styles.input}
        onClick={handleOpen}
        disabled={disabled}
      >
        <Icon name="calendar" size="sm" color="disable" />
        <span className={displayValue ? styles.inputText : styles.inputPlaceholder}>
          {displayValue || placeholder}
        </span>
      </button>

      {error && (
        <div className={styles.errorContainer}>
          <Icon name="triangleAlert" color="error" size="sm" />
          <p className={styles.errorText}>{error}</p>
        </div>
      )}

      {isOpen && (
        <div className={classNames(styles.popover, { [styles.popoverAlignRight]: alignRight })}>
          <div className={styles.popoverHeader}>
            <span className={styles.headerTitle}>
              {step === 'year' ? 'Year' : 'Month'}
            </span>
          </div>

          {step === 'year' && (
            <div className={styles.gridScrollable}>
              {years.map((year) => {
                const isSelected = tempYear === year;
                const yearDisabled = isYearDisabled(year);
                return (
                  <button
                    key={year}
                    ref={isSelected ? selectedYearRef : undefined}
                    type="button"
                    className={classNames(styles.gridItem, { [styles.gridItemSelected]: isSelected })}
                    onClick={() => handleYearClick(year)}
                    disabled={yearDisabled}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          )}

          {step === 'month' && (
            <div className={styles.grid}>
              {MONTHS_SHORT.map((m, i) => {
                const month = i + 1;
                const isSelected = value?.month === month && value?.year === tempYear;
                const monthDisabled = isMonthDisabled(month);
                return (
                  <button
                    key={m}
                    type="button"
                    className={classNames(styles.gridItem, { [styles.gridItemSelected]: isSelected })}
                    onClick={() => handleMonthClick(month)}
                    disabled={monthDisabled}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
